// Pure model of the timesheets screens (PLN-FE-04): week arithmetic, the grid lines built from the
// entries, parsing and formatting of durations (15-minute steps), the timer's rounding and its
// storage, and the capacity states. Clock-free: `now` is always passed in.
import type { MyWeekDtoOutput, MyWeekDtoOutputEntriesItem } from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import { TIME_CATEGORIES, TIME_STEP_MINUTES, type TimeCategory } from '@batiflow/contracts';
import { intlLocale, type AppLocale } from '@batiflow/i18n';
import type { TFunction } from 'i18next';

export type Entry = MyWeekDtoOutputEntriesItem;
export type Week = MyWeekDtoOutput;
export type Translate = TFunction<'web'>;

export const CATEGORIES: readonly TimeCategory[] = TIME_CATEGORIES;
export const STEP = TIME_STEP_MINUTES;
/** Longest contract week the API accepts (70 hours). */
export const MAX_WEEKLY_MINUTES = 4200;
export const MAX_DAY_MINUTES = 1440;
/** Above this share of capacity (110 %), in basis points, a week is overloaded. */
export const OVERLOAD_BP = 11_000;
export const DEFAULT_CAPACITY_WEEKS = 4;
export const MAX_CAPACITY_WEEKS = 12;

export const TABS = ['week', 'approvals', 'capacity', 'leave'] as const;
export type TimesheetTab = (typeof TABS)[number];
export const isTab = (value: unknown): value is TimesheetTab =>
  typeof value === 'string' && (TABS as readonly string[]).includes(value);

export const isCategory = (value: unknown): value is TimeCategory =>
  typeof value === 'string' && (CATEGORIES as readonly string[]).includes(value);

// --- Dates (YYYY-MM-DD, calendar arithmetic in UTC: no time zone shifts).

const DAY_MS = 86_400_000;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isDay(value: unknown): value is string {
  if (typeof value !== 'string' || !DAY_RE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
}

export const addDays = (day: string, days: number): string =>
  new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);

/** The Monday of the week of `day`. */
export function mondayOf(day: string): string {
  const weekday = new Date(`${day}T12:00:00Z`).getUTCDay(); // 0 = Sunday
  return addDays(day, weekday === 0 ? -6 : 1 - weekday);
}

export const isMonday = (value: unknown): value is string =>
  isDay(value) && new Date(`${value}T12:00:00Z`).getUTCDay() === 1;

/** Today in Tunisia (`YYYY-MM-DD`). */
export function tunisToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Tunis',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export const weekDays = (weekStart: string): string[] =>
  Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));

/** "lun." and "12 oct." in the reader's language. */
export function weekdayLabel(day: string, locale: AppLocale): { weekday: string; date: string } {
  const at = new Date(`${day}T12:00:00Z`);
  const tag = intlLocale(locale);
  return {
    weekday: new Intl.DateTimeFormat(tag, { weekday: 'short', timeZone: 'UTC' }).format(at),
    date: new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
      at,
    ),
  };
}

/** "12 oct. – 18 oct. 2026". */
export function weekRangeLabel(weekStart: string, locale: AppLocale): string {
  const tag = intlLocale(locale);
  const format = (day: string, withYear: boolean) =>
    new Intl.DateTimeFormat(tag, {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    }).format(new Date(`${day}T12:00:00Z`));
  return `${format(weekStart, false)} – ${format(addDays(weekStart, 6), true)}`;
}

/** Whether a day is a Saturday or a Sunday. */
export function isWeekend(day: string): boolean {
  const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
  return weekday === 0 || weekday === 6;
}

// --- Durations.

/** `h:mm` ("7:30"); empty for zero. */
export function clockOf(minutes: number): string {
  if (minutes <= 0) return '';
  return `${String(Math.floor(minutes / 60))}:${String(minutes % 60).padStart(2, '0')}`;
}

/** The pieces of "32 h" / "7 h 30": the caller builds the sentence in the reader's language. */
export function hoursAndMinutes(minutes: number): { h: number; m: number } {
  return { h: Math.floor(minutes / 60), m: minutes % 60 };
}

export type ParsedDuration =
  { ok: true; minutes: number } | { ok: false; reason: 'format' | 'step' | 'max' };

/**
 * A duration typed in a cell: `7:30`, `7,5` / `7.5` (hours), `2h30`, `2h`, `45m`, `45min`, or a bare
 * number of hours ("4"). Empty or zero clears the cell (`minutes: 0`). Only 15-minute steps up to
 * 24 hours are accepted; nothing is rounded silently.
 */
export function parseDuration(input: string, max: number = MAX_DAY_MINUTES): ParsedDuration {
  const text = input.trim().toLowerCase().replace(/\s+/g, '');
  if (text === '') return { ok: true, minutes: 0 };
  let minutes: number | null = null;
  const clock = /^(\d{1,2}):(\d{2})$/.exec(text);
  const hm = /^(\d{1,2})h(\d{0,2})$/.exec(text);
  const mins = /^(\d{1,4})(?:m|min)$/.exec(text);
  const decimal = /^(\d{1,2})[.,]?(\d{0,2})$/.exec(text);
  if (clock) {
    if (Number(clock[2]) > 59) return { ok: false, reason: 'format' };
    minutes = Number(clock[1]) * 60 + Number(clock[2]);
  } else if (hm) {
    if (Number(hm[2]) > 59) return { ok: false, reason: 'format' };
    minutes = Number(hm[1]) * 60 + Number(hm[2]);
  } else if (mins) {
    minutes = Number(mins[1]);
  } else if (decimal) {
    minutes = Math.round(Number(`${decimal[1] ?? '0'}.${decimal[2] ?? ''}0`) * 60);
  }
  if (minutes === null) return { ok: false, reason: 'format' };
  if (minutes > max) return { ok: false, reason: 'max' };
  if (minutes % STEP !== 0) return { ok: false, reason: 'step' };
  return { ok: true, minutes };
}

// --- Grid lines.

export interface LineKey {
  projectId: string;
  phaseId: string | null;
  category: TimeCategory;
}

export const lineId = (key: LineKey): string =>
  `${key.projectId}|${key.phaseId ?? '-'}|${key.category}`;

export interface GridLine extends LineKey {
  id: string;
  projectName: string;
  projectCode: string | null;
}

export interface GridCell {
  entries: Entry[];
  minutes: number;
  /** The one entry of the cell when it can be edited; null when empty, decided or several. */
  editable: Entry | null;
  /** The cell is empty or holds one draft/rejected entry (and the week is open). */
  canEdit: boolean;
  rejected: Entry | null;
}

export const isOpenStatus = (entry: Pick<Entry, 'status'>): boolean =>
  entry.status === 'draft' || entry.status === 'rejected';

/** The lines of the week: those of its entries plus the lines the person added (kept empty). */
export function buildLines(entries: readonly Entry[], extra: readonly GridLine[]): GridLine[] {
  const lines = new Map<string, GridLine>();
  for (const entry of entries) {
    const key: LineKey = {
      projectId: entry.projectId,
      phaseId: entry.phaseId,
      category: entry.category,
    };
    const id = lineId(key);
    if (!lines.has(id)) {
      lines.set(id, { ...key, id, projectName: entry.projectName, projectCode: entry.projectCode });
    }
  }
  for (const line of extra) if (!lines.has(line.id)) lines.set(line.id, line);
  return [...lines.values()].sort(
    (a, b) =>
      (a.projectCode ?? a.projectName).localeCompare(b.projectCode ?? b.projectName) ||
      (a.phaseId ?? '').localeCompare(b.phaseId ?? '') ||
      CATEGORIES.indexOf(a.category) - CATEGORIES.indexOf(b.category),
  );
}

export function cellOf(
  entries: readonly Entry[],
  line: LineKey,
  day: string,
  locked: boolean,
): GridCell {
  const here = entries.filter(
    (entry) =>
      entry.projectId === line.projectId &&
      entry.phaseId === line.phaseId &&
      entry.category === line.category &&
      entry.workDate === day,
  );
  const minutes = here.reduce((sum, entry) => sum + entry.minutes, 0);
  const only = here.length === 1 ? (here[0] ?? null) : null;
  const editable = only && isOpenStatus(only) && !locked ? only : null;
  return {
    entries: here,
    minutes,
    editable,
    canEdit: !locked && (here.length === 0 || editable !== null),
    rejected: here.find((entry) => entry.status === 'rejected') ?? null,
  };
}

export const dayTotal = (entries: readonly Entry[], day: string): number =>
  entries.filter((entry) => entry.workDate === day).reduce((sum, entry) => sum + entry.minutes, 0);

/** Entries a person can still submit: drafts and rejected ones. */
export const submittable = (entries: readonly Entry[]): Entry[] => entries.filter(isOpenStatus);

// --- Timer.

export interface TimerState extends LineKey {
  /** Epoch milliseconds when it started. */
  startedAt: number;
}

/** Elapsed time rounded UP to the next 15 minutes (at least one step). */
export function roundedMinutes(elapsedMs: number): number {
  const minutes = Math.ceil(Math.max(0, elapsedMs) / 60_000);
  return Math.min(MAX_DAY_MINUTES, Math.max(STEP, Math.ceil(minutes / STEP) * STEP));
}

export const timerStorageKey = (orgId: string): string => `batiflow.timesheets.timer.${orgId}`;

export function loadTimer(orgId: string): TimerState | null {
  try {
    const raw = window.localStorage.getItem(timerStorageKey(orgId));
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<TimerState> | null;
    if (
      value &&
      typeof value.projectId === 'string' &&
      (typeof value.phaseId === 'string' || value.phaseId === null) &&
      isCategory(value.category) &&
      typeof value.startedAt === 'number' &&
      Number.isFinite(value.startedAt)
    ) {
      return {
        projectId: value.projectId,
        phaseId: value.phaseId,
        category: value.category,
        startedAt: value.startedAt,
      };
    }
  } catch {
    // Storage unavailable or corrupt: no timer.
  }
  return null;
}

export function saveTimer(orgId: string, timer: TimerState | null): void {
  try {
    if (timer) window.localStorage.setItem(timerStorageKey(orgId), JSON.stringify(timer));
    else window.localStorage.removeItem(timerStorageKey(orgId));
  } catch {
    // Storage unavailable: the timer lives for this page only.
  }
}

/** "01:07:09" for the running timer. */
export function elapsedClock(elapsedMs: number): string {
  const seconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
}

// --- Capacity.

export type LoadState = 'none' | 'ok' | 'high' | 'over';

/** No capacity → `none`; up to 100 % `ok`; 100–110 % `high`; above 110 % `over`. */
export function loadState(cell: { loadBp: number | null; overloaded: boolean }): LoadState {
  if (cell.loadBp === null) return 'none';
  if (cell.overloaded || cell.loadBp > OVERLOAD_BP) return 'over';
  return cell.loadBp > 10_000 ? 'high' : 'ok';
}

/** Whole percent of a basis-point share (rounded). */
export const percentOf = (bp: number): number => Math.round(bp / 100);

// --- Errors.

export const apiErrorCode = (error: unknown): string | null =>
  error instanceof ApiError ? error.code : null;

/** A plain-language message for an API error (never the raw server text). */
export function errorMessage(t: Translate, error: unknown): string {
  if (!(error instanceof ApiError)) return t('timesheets.errors.network');
  return t(`timesheets.errors.${error.code}`, {
    defaultValue: t(`common:errors.${error.code}`, { defaultValue: t('common:errors.generic') }),
  });
}

export const etagOf = (version: number): string => `W/"${String(version)}"`;
